import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  listSuppliers,
  listSupplierProducts,
  updateProduct,
  bulkUpdateProducts,
  patchProductStatus,
  deleteProduct,
  fetchDosageForms,
  fetchStrengthUnits,
  fetchHqCountries,
  fetchSupplierNames,
} from '../services/supplierService';
import { useMsal } from '@azure/msal-react';
import { fetchDashboardStats, getDashboardToken } from '../services/dashboardService';
import { fetchRegisterCounts } from '../services/externalRegisterService';
import { useSearch } from '../context/useSearch';
import useUser from '../auth/useUser';
import AutocompleteInput from '../components/AutocompleteInput';
import MultiSelect from '../components/MultiSelect';
import SearchableSelect from '../components/SearchableSelect';
import SupplierInfoModal from '../components/modals/SupplierInfoModal';
import HistoricalDataModal from '../components/modals/HistoricalDataModal';
import './Suppliers.css';

/* ---------- Helpers ---------- */

const fmtDate = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const truncate = (s, n) => {
  if (!s) return '';
  const str = String(s);
  return str.length > n ? str.slice(0, n) + '…' : str;
};

const arrOrStr = (v) => {
  if (!v) return '';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
};

const statusFromCount = (count) => {
  if (count > 200) return { label: 'Active', cls: 'ok' };
  if (count >= 100) return { label: 'Partial', cls: 'warn' };
  return { label: 'Update needed', cls: 'alert' };
};

const progressClass = (pct) => {
  if (pct > 70) return 'good';
  if (pct >= 40) return 'mid';
  return 'bad';
};

const maBadgeClass = (v) => {
  if (!v) return null;
  const val = String(v).toLowerCase();
  if (val.includes('market')) return 'green';
  if (val.includes('in progress')) return 'amber';
  if (val.includes('pending')) return 'blue';
  if (val.includes('expected')) return 'orange-text';
  return null;
};

const isNL = (countries) => {
  if (!countries) return false;
  const v = String(countries).toLowerCase();
  return v.includes('nl') || v.includes('netherlands');
};

const uniqueValues = (rows, key) => {
  const set = new Set();
  for (const r of rows) {
    const v = r[key];
    if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
};

const STATUS_OPTIONS = ['Pending', 'Approved', 'Rejected'];
const normalizeStatus = (v) => {
  if (!v || !String(v).trim()) return 'Pending';
  const lc = String(v).trim().toLowerCase();
  if (lc === 'pending') return 'Pending';
  if (lc === 'approved') return 'Approved';
  if (lc === 'rejected') return 'Rejected';
  return 'Pending';
};
const statusColor = (v) => {
  if (v === 'Approved') return 'var(--green)';
  if (v === 'Rejected') return 'var(--red)';
  return 'var(--t3)';
};

/* ---------- Skeletons ---------- */

function CardSkeleton() {
  return (
    <div className="sup-card">
      <div className="d-flex align-items-center gap-2 mb-3">
        <div className="sup-skeleton" style={{ width: 44, height: 44, borderRadius: '50%' }} />
        <div className="flex-grow-1">
          <div className="sup-skeleton" style={{ height: 14, width: '70%', marginBottom: 6 }} />
          <div className="sup-skeleton" style={{ height: 10, width: '50%' }} />
        </div>
      </div>
      <div className="sup-skeleton" style={{ height: 6, width: '100%', marginBottom: 14 }} />
      <div className="sup-skeleton" style={{ height: 20, width: '60%' }} />
    </div>
  );
}

/* ---------- Supplier card ---------- */

function SupplierCard({ supplier, maxCount, onOpen, onInfo }) {
  const { supplier_name, product_count, last_list_date } = supplier;
  const status = statusFromCount(product_count);
  const pct = maxCount > 0 ? Math.min(100, Math.round((product_count / maxCount) * 100)) : 0;
  const barCls = progressClass(pct);
  const initial = (supplier_name || '?').trim().charAt(0).toUpperCase();

  const tags = useMemo(() => {
    if (!supplier.atc_sample) return [];
    return String(supplier.atc_sample)
      .split(/[|,;]+/)
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 3);
  }, [supplier.atc_sample]);

  const handleInfoClick = (e) => {
    e.stopPropagation();
    onInfo(supplier_name);
  };

  return (
    <div
      className="sup-card"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(supplier_name)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpen(supplier_name); }}
      style={{ position: 'relative' }}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <div className="sup-avatar">{initial}</div>
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div className="sup-name text-truncate">{supplier_name}</div>
          <div className="sup-meta">— · {product_count} products</div>
        </div>
        <span className={`sup-status ${status.cls}`}>{status.label}</span>
      </div>

      <div className="sup-progress-wrap">
        <div className={`sup-progress ${barCls}`}>
          <span style={{ width: `${pct}%` }} />
        </div>
        <span className="sup-pct">{pct}% NL</span>
      </div>

      {tags.length > 0 && (
        <div className="sup-tags">
          {tags.map((t, i) => (
            <span key={i} className="sup-tag">{truncate(t, 22)}</span>
          ))}
        </div>
      )}

      <div className="sup-last">Last list: {fmtDate(last_list_date)}</div>

      <button
        type="button"
        className="sup-info-btn"
        onClick={handleInfoClick}
        title="Supplier information"
        aria-label="Supplier information"
      >
        <i className="bi bi-info-circle-fill"></i>
      </button>
    </div>
  );
}

/* ---------- Single edit modal ---------- */

const PATENT_OPTIONS = ['Patented', 'Off Patent', 'Patent Expiring', 'Unknown'];

const DOSSIER_DATE_OPTIONS = ['Available',
  '2021-Q1','2021-Q2','2021-Q3','2021-Q4','2022-Q1','2022-Q2','2022-Q3','2022-Q4',
  '2023-Q1','2023-Q2','2023-Q3','2023-Q4','2024-Q1','2024-Q2','2024-Q3','2024-Q4',
  '2025-Q1','2025-Q2','2025-Q3','2025-Q4','2026-Q1','2026-Q2','2026-Q3','2026-Q4',
  '2027-Q1','2027-Q2','2027-Q3','2027-Q4','2028-Q1','2028-Q2','2028-Q3','2028-Q4',
  '2029-Q1','2029-Q2','2029-Q3','2029-Q4','2030-Q1','2030-Q2','2030-Q3','2030-Q4'];

function isValidDate(s) {
  if (!s) return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s)) && !Number.isNaN(new Date(s).getTime());
}

const SECTION_TINTS = {
  general: '#eef4fb',
  market: '#f0f7f0',
  atc: '#fdf6ec',
  manufacturing: '#f5f0f8',
  other: '#f3f4f6',
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function SectionHeading({ title }) {
  return (
    <div className="col-12" style={{ borderTop: '1px solid #dee2e6', paddingTop: 12, marginTop: 4 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
    </div>
  );
}

function SectionBlock({ bg, children }) {
  return (
    <div className="col-12" style={{ background: bg, borderRadius: 7, padding: '14px 14px 6px' }}>
      <div className="row g-3">{children}</div>
    </div>
  );
}

const XREF_TINT = '#fef9f3';
const SOURCE_LABELS = { internal: 'FocusOne', ema: 'EMA Register', dutch: 'Dutch CBG-MEB', ansm: 'ANSM France' };

function splitInns(inn, combines) {
  if (!inn || !String(inn).trim()) return [];
  const s = String(inn).trim();
  if (combines === 'Yes' || s.includes('+') || s.includes(',')) {
    return s.split(/\s*[+,/]\s*/).map(v => v.trim()).filter(Boolean);
  }
  return [s];
}

function CrossReferenceSection({ inn, combines }) {
  const inns = useMemo(() => splitInns(inn, combines), [inn, combines]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (inns.length === 0) return;
    setLoading(true);
    const params = new URLSearchParams();
    inns.forEach(i => params.append('inn', i));
    fetch(`${import.meta.env.VITE_API_URL}/api/cross-reference/summary?${params}`)
      .then(r => r.json())
      .then(d => setData(d.results || []))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [inns]);

  if (inns.length === 0) {
    return (
      <SectionBlock bg={XREF_TINT}>
        <SectionHeading title="Cross-Reference" />
        <div className="col-12" style={{ fontSize: 13, color: 'var(--t3)', paddingBottom: 8 }}>No INN available for cross-reference</div>
      </SectionBlock>
    );
  }

  return (
    <>
      <SectionBlock bg={XREF_TINT}>
        <SectionHeading title="Cross-Reference" />
        <div className="col-12" style={{ paddingBottom: 8 }}>
          {loading && <div style={{ fontSize: 13, color: 'var(--t3)' }}>Loading cross-reference data...</div>}
          {!loading && data && data.map((entry, idx) => (
            <div key={idx} style={{ marginBottom: idx < data.length - 1 ? 10 : 0 }}>
              {['internal', 'ema', 'dutch', 'ansm'].map(src => (
                <div key={src} style={{ fontSize: 12.5, color: 'var(--t1)', lineHeight: 1.7 }}>
                  <strong>{entry.inn}</strong> found in {entry[src].product_count} products – From {entry[src].supplier_count} different suppliers in {SOURCE_LABELS[src]}
                </div>
              ))}
            </div>
          ))}
          {!loading && data && (
            <button
              type="button"
              onClick={() => setShowDetail(true)}
              style={{ marginTop: 6, background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', fontSize: 12.5, fontWeight: 500, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <i className="bi bi-caret-right-fill" style={{ fontSize: 10 }}></i> Click here to see details
            </button>
          )}
        </div>
      </SectionBlock>
      {showDetail && <CrossReferenceDetailModal inns={inns} onClose={() => setShowDetail(false)} />}
    </>
  );
}

function CrossReferenceDetailModal({ inns, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    inns.forEach(i => params.append('inn', i));
    fetch(`${import.meta.env.VITE_API_URL}/api/cross-reference/details?${params}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setData({ internal: [], ema: [], dutch: [], ansm: [] }))
      .finally(() => setLoading(false));
  }, [inns]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const title = inns.join(' + ');

  const renderTable = (label, rows) => (
    <div style={{ flex: '1 1 48%', minWidth: 320, background: 'var(--card)', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>{label}</div>
      {rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>No matches found in {label}</div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>INN</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Dosage Form</th>
                <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Supplier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '5px 10px' }}>{r.inn || '—'}</td>
                  <td style={{ padding: '5px 10px' }}>{r.dosage_form || '—'}</td>
                  <td style={{ padding: '5px 10px' }}>{r.supplier || '—'}</td>
                </tr>
              ))}
              {rows.length >= 500 && (
                <tr><td colSpan={3} style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--t3)', fontSize: 11, fontStyle: 'italic' }}>Showing 500 results — refine your search</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1070, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'xrefFadeIn 200ms ease-out' }}
    >
      <style>{`
        @keyframes xrefFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes xrefScaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '90%', maxWidth: 1400, maxHeight: '85vh', background: '#fff', borderRadius: 14, display: 'flex', flexDirection: 'column', animation: 'xrefScaleIn 250ms ease-out' }}
      >
        <div className="sup-modal-head" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '14px 14px 0 0' }}>
          <h5 className="sup-modal-title mb-0">Cross-Reference Details for {title}</h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--t3)' }}>
              <div className="spinner-border spinner-border-sm me-2" role="status" />
              Loading cross-reference data...
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              {renderTable('Internal Data (FocusOne)', data?.internal || [])}
              {renderTable('EMA Register', data?.ema || [])}
              {renderTable('Dutch CBG-MEB Data', data?.dutch || [])}
              {renderTable('ANSM France', data?.ansm || [])}
            </div>
          )}
        </div>
        <div className="sup-modal-foot" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff', borderRadius: '0 0 14px 14px' }}>
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
  );
}

function SingleEditModal({ row, onClose, onSaved, onToast, dosageForms, strengthUnits, editorName }) {
  const rawDossierDate = row.dossier_preparation_planned_date || '';
  const dossierDateMatches = DOSSIER_DATE_OPTIONS.includes(rawDossierDate);
  const isInactive = row.product_inactive === 'Yes';
  const [form, setForm] = useState({
    supplier_product_name: row.supplier_product_name || '',
    inn: row.inn || '',
    combines_molecules: row.combines_molecules || 'No',
    dosage_form: row.dosage_form || '',
    strength_value_1: row.strength_value_1 ?? '',
    strength_unit_code_1: row.strength_unit_code_1 || '',
    pack_size: row.pack_size || '',
    reference_product_name: row.reference_product_name || '',
    market_auth_countries: row.market_auth_countries || '',
    nl_market_available: row.nl_market_available || '',
    nl_general_ma_status: row.nl_general_ma_status || '',
    supplier_ma_status: row.supplier_ma_status || '',
    ma_registration_number: row.ma_registration_number || '',
    ma_approved_by: row.ma_approved_by || '',
    ma_additional_info: row.ma_additional_info || '',
    dossier_availability_status: row.dossier_availability_status || '',
    dossier_preparation_planned_date: dossierDateMatches ? rawDossierDate : '',
    atc_code: row.atc_code || '',
    atc_classification_name: row.atc_classification_name || '',
    atc_category: row.atc_category || '',
    manufacturing_sites: row.manufacturing_sites || '',
    patent_status: row.patent_status || 'Unknown',
    patent_expiry_date: row.patent_expiry_date || '',
    patent_notes: row.patent_notes || '',
    therapeutical_indication: arrOrStr(row.therapeutical_indication),
    additional_comments: arrOrStr(row.additional_comments),
  });
  const [saving, setSaving] = useState(false);
  const [dossierDateTouched, setDossierDateTouched] = useState(false);
  const [reactivationConfirmed, setReactivationConfirmed] = useState(false);
  const [showReactivateConfirm, setShowReactivateConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const dis = isInactive && !reactivationConfirmed;
  const disStyle = { color: 'var(--t3)', background: '#f8f9fa' };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const onChange = (k) => (e) => set(k, e.target.value);

  const save = async () => {
    setSaving(true);
    try {
      if (isInactive && reactivationConfirmed) {
        const updated = await updateProduct(row.id || row.product_id, { product_inactive: 'No' }, editorName);
        onSaved(updated);
        onToast({ type: 'ok', msg: 'Product reactivated successfully' });
      } else {
        const payload = { ...form };
        if (payload.therapeutical_indication)
          payload.therapeutical_indication = String(payload.therapeutical_indication)
            .split('\n').map(s => s.trim()).filter(Boolean);
        if (payload.additional_comments)
          payload.additional_comments = String(payload.additional_comments)
            .split('\n').map(s => s.trim()).filter(Boolean);
        if (!isValidDate(payload.patent_expiry_date)) delete payload.patent_expiry_date;
        if (!dossierDateTouched || !payload.dossier_preparation_planned_date) delete payload.dossier_preparation_planned_date;
        const updated = await updateProduct(row.id || row.product_id, payload, editorName);
        onSaved(updated);
        onToast({ type: 'ok', msg: 'Product updated successfully' });
      }
    } catch {
      onToast({ type: 'error', msg: 'Failed to update product' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '14px 14px 0 0' }}>
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">Edit Product</span>
            <span className="sup-title-sub">{row.supplier_product_name || '—'}</span>
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="sup-modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {isInactive && (
            <div style={{ background: '#fef2f2', color: '#991b1b', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13, border: '1px solid #fecaca' }}>
              This product was marked inactive on {fmtDateTime(row.product_deletion_date)} by {row.deleted_by || '—'}. Editing is disabled. Set Active / Inactive to Active to reactivate.
            </div>
          )}
          <div className="row g-3">
            {/* ──── Cross-Reference (read-only) ──── */}
            <CrossReferenceSection inn={row.inn} combines={row.combines_molecules} />

            <SectionBlock bg={SECTION_TINTS.general}>
              <SectionHeading title="General Information" />
              <Field label="Supplier Product Name">
                <input className="sup-input" value={form.supplier_product_name} onChange={onChange('supplier_product_name')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="INN">
                <input className="sup-input" value={form.inn} onChange={onChange('inn')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Combines Molecules">
                <select className="sup-select" value={form.combines_molecules} onChange={onChange('combines_molecules')} disabled={dis} style={dis ? disStyle : undefined}>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </Field>
              <Field label="Dosage Form">
                {dis ? <input className="sup-input" value={form.dosage_form} disabled style={disStyle} /> : (
                  <AutocompleteInput value={form.dosage_form} onChange={(v) => set('dosage_form', v)} suggestions={dosageForms} placeholder="Start typing…" />
                )}
              </Field>
              <Field label="Strength Value">
                <input className="sup-input" value={form.strength_value_1} onChange={onChange('strength_value_1')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Strength Unit Code">
                {dis ? <input className="sup-input" value={form.strength_unit_code_1} disabled style={disStyle} /> : (
                  <AutocompleteInput value={form.strength_unit_code_1} onChange={(v) => set('strength_unit_code_1', v)} suggestions={strengthUnits} placeholder="Start typing…" />
                )}
              </Field>
              <Field label="Pack Size">
                <input className="sup-input" value={form.pack_size} onChange={onChange('pack_size')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Reference Product">
                <input className="sup-input" value={form.reference_product_name} onChange={onChange('reference_product_name')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
            </SectionBlock>
            <SectionBlock bg={SECTION_TINTS.market}>
              <SectionHeading title="Market Details" />
              <Field label="Market Auth Countries" wide>
                <input className="sup-input" value={form.market_auth_countries} onChange={onChange('market_auth_countries')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="NL Market">
                <select className="sup-select" value={form.nl_market_available} onChange={onChange('nl_market_available')} disabled={dis} style={dis ? disStyle : undefined}>
                  <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                </select>
              </Field>
              <Field label="NL MA Status">
                <select className="sup-select" value={form.nl_general_ma_status} onChange={onChange('nl_general_ma_status')} disabled={dis} style={dis ? disStyle : undefined}>
                  <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                </select>
              </Field>
              <Field label="Supplier MA Status">
                <input className="sup-input" value={form.supplier_ma_status} onChange={onChange('supplier_ma_status')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="MA Reg. Number">
                <input className="sup-input" value={form.ma_registration_number} onChange={onChange('ma_registration_number')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="MA Approval">
                <input className="sup-input" value={form.ma_approved_by} onChange={onChange('ma_approved_by')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="MA Additional Info" wide>
                <input className="sup-input" value={form.ma_additional_info} onChange={onChange('ma_additional_info')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Dossier Availability Status">
                <input className="sup-input" value={form.dossier_availability_status} onChange={onChange('dossier_availability_status')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Dossier Planned Date">
                {dis ? <input className="sup-input" value={form.dossier_preparation_planned_date || '—'} disabled style={disStyle} /> : (
                  <>
                    <SearchableSelect options={DOSSIER_DATE_OPTIONS} value={form.dossier_preparation_planned_date} onChange={(v) => { set('dossier_preparation_planned_date', v); setDossierDateTouched(true); }} placeholder="Select quarter…" />
                    {rawDossierDate && !dossierDateMatches && !dossierDateTouched && (
                      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 3 }}>Existing value: &apos;{rawDossierDate}&apos; — pick an option to overwrite, or leave unselected to keep.</div>
                    )}
                  </>
                )}
              </Field>
            </SectionBlock>
            <SectionBlock bg={SECTION_TINTS.atc}>
              <SectionHeading title="ATC Classification" />
              <Field label="ATC Code">
                <input className="sup-input" value={form.atc_code} onChange={onChange('atc_code')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="ATC Classification">
                <input className="sup-input" value={form.atc_classification_name} onChange={onChange('atc_classification_name')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="ATC Category">
                <input className="sup-input" value={form.atc_category} onChange={onChange('atc_category')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
            </SectionBlock>
            <SectionBlock bg={SECTION_TINTS.manufacturing}>
              <SectionHeading title="Manufacturing and Patent" />
              <Field label="Manufacturing Sites" wide>
                <input className="sup-input" value={form.manufacturing_sites} onChange={onChange('manufacturing_sites')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Patent Status">
                <select className="sup-select" value={form.patent_status} onChange={onChange('patent_status')} disabled={dis} style={dis ? disStyle : undefined}>
                  {PATENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Patent Expiry Date">
                {dis ? <input className="sup-input" value={form.patent_expiry_date || '—'} disabled style={disStyle} /> : (
                  <>
                    <input type="date" className="sup-input" value={isValidDate(form.patent_expiry_date) ? form.patent_expiry_date : ''} onChange={(e) => set('patent_expiry_date', e.target.value)} />
                    {form.patent_expiry_date && !isValidDate(form.patent_expiry_date) && (
                      <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 3 }}>Existing value: &apos;{form.patent_expiry_date}&apos; — pick a date to overwrite, or leave empty to keep.</div>
                    )}
                  </>
                )}
              </Field>
              <Field label="Patent Notes" wide>
                <input className="sup-input" value={form.patent_notes} onChange={onChange('patent_notes')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
            </SectionBlock>
            <SectionBlock bg={SECTION_TINTS.other}>
              <SectionHeading title="Other Details" />
              <Field label="Therapeutical Indication" wide>
                <textarea className="sup-textarea" value={form.therapeutical_indication} onChange={onChange('therapeutical_indication')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Additional Info" wide>
                <textarea className="sup-textarea" value={form.additional_comments} onChange={onChange('additional_comments')} disabled={dis} style={dis ? disStyle : undefined} />
              </Field>
              <Field label="Creation Date">
                <input className="sup-input" value={row.created_at ? fmtDate(row.created_at) : '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Created From">
                <input className="sup-input" value={row.document_name_reference || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Last Import Date">
                <input className="sup-input" value={row.updated_at ? fmtDate(row.updated_at) : '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Last Import List">
                <input className="sup-input" value={row.document_name_reference || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Updated by">
                <input className="sup-input" value={row.created_by || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Active / Inactive">
                <select className="sup-select" value={isInactive && !reactivationConfirmed ? 'Inactive' : 'Active'} disabled={!isInactive || reactivationConfirmed} style={!isInactive || reactivationConfirmed ? { color: 'var(--t3)', background: '#f8f9fa' } : undefined} onChange={(e) => { if (e.target.value === 'Active' && isInactive) setShowReactivateConfirm(true); }}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </Field>
              <Field label="Deletion Date">
                <input className="sup-input" value={row.product_deletion_date ? fmtDateTime(row.product_deletion_date) : '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
              <Field label="Deleted By">
                <input className="sup-input" value={row.deleted_by || '—'} disabled style={{ color: 'var(--t3)', background: '#f8f9fa' }} />
              </Field>
            </SectionBlock>
          </div>
        </div>
        <div className="sup-modal-foot" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff', borderRadius: '0 0 14px 14px', justifyContent: 'space-between' }}>
          <button type="button" className="sup-btn" onClick={() => setShowHistory(true)} style={{ marginRight: 'auto' }}>
            <i className="bi bi-clock-history me-1"></i> Historical Data
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
              <i className="bi bi-x-circle me-1"></i> {isInactive && !reactivationConfirmed ? 'Close' : 'Cancel'}
            </button>
            {(!isInactive || reactivationConfirmed) && (
              <button type="button" className="sup-btn primary" onClick={save} disabled={saving}>
                <i className="bi bi-check-circle me-1"></i> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
      {showHistory && (
        <HistoricalDataModal
          productId={row.product_id}
          moleculeName={row.molecule_name}
          inn={row.inn}
          supplierName={row.supplier_name}
          onClose={() => setShowHistory(false)}
        />
      )}
      {showReactivateConfirm && (
        <div className="sup-modal-backdrop" style={{ zIndex: 1060 }} onClick={() => setShowReactivateConfirm(false)}>
          <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Reactivate this product?</h5>
              <button type="button" className="sup-modal-close" onClick={() => setShowReactivateConfirm(false)}>×</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              <strong>{row.supplier_product_name || '—'}</strong> (ID: {row.id || row.product_id}) will become active again and editable. The deletion record will be cleared.
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setShowReactivateConfirm(false)}>Cancel</button>
              <button type="button" className="sup-btn primary" onClick={() => { setReactivationConfirmed(true); setShowReactivateConfirm(false); }}>Yes, Reactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? 'col-12' : 'col-12 col-md-6'}>
      <div className="sup-field-label">{label}</div>
      {children}
    </div>
  );
}

/* ---------- Remove confirm modal ---------- */

function RemoveConfirmModal({ row, onConfirm, onCancel }) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="sup-modal-backdrop" onClick={onCancel}>
      <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">Remove this product?</h5>
          <button type="button" className="sup-modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="sup-modal-body" style={{ fontSize: 14 }}>
          <strong>{row.supplier_product_name || '—'}</strong> (ID: {row.id || row.product_id}) will be marked as inactive. You can reactivate later from the Edit modal if needed.
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="sup-btn primary" disabled={saving} onClick={async () => {
            setSaving(true);
            await onConfirm();
            setSaving(false);
          }}>{saving ? 'Removing…' : 'Yes, Remove'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Status confirm modal ---------- */

function StatusConfirmModal({ productName, newStatus, onConfirm, onCancel }) {
  const [saving, setSaving] = useState(false);
  return (
    <div className="sup-modal-backdrop" onClick={onCancel}>
      <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">Confirm Status Change</h5>
          <button type="button" className="sup-modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="sup-modal-body" style={{ fontSize: 14 }}>
          Are you sure you want to change the status of <strong>{productName || 'this product'}</strong> to <strong>{newStatus}</strong>?
          This update will be saved immediately.
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="button" className="sup-btn primary" disabled={saving} onClick={async () => {
            setSaving(true);
            await onConfirm();
            setSaving(false);
          }}>{saving ? 'Saving…' : 'Yes, change status'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Bulk edit modal ---------- */

function BulkEditModal({ count, ids, onClose, onSaved, onToast, editorName }) {
  const [enabled, setEnabled] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [supplierOpts, setSupplierOpts] = useState(null);
  const [dosageOpts, setDosageOpts] = useState(null);
  const [unitOpts, setUnitOpts] = useState(null);

  useEffect(() => {
    fetchSupplierNames().then(setSupplierOpts).catch(() => setSupplierOpts([]));
    fetchDosageForms().then(setDosageOpts).catch(() => setDosageOpts([]));
    fetchStrengthUnits().then(setUnitOpts).catch(() => setUnitOpts([]));
  }, []);

  const BULK_FIELDS = useMemo(() => [
    { key: 'product_status',              label: 'Product Status',             type: 'select', options: ['Pending', 'Approved', 'Rejected'] },
    { key: 'supplier_name',               label: 'Supplier Name',              type: 'select', options: supplierOpts },
    { key: 'dosage_form',                 label: 'Dosage Form',                type: 'select', options: dosageOpts },
    { key: 'strength_unit_code_1',        label: 'Strength Unit',              type: 'select', options: unitOpts },
    { key: 'combines_molecules',          label: 'Combine Molecule',           type: 'select', options: ['Yes', 'No'] },
    { key: 'supplier_ma_status',          label: 'MA Status',                  type: 'text' },
    { key: 'nl_general_ma_status',        label: 'NL Market Available',        type: 'select', options: ['Yes', 'No'] },
    { key: 'dossier_availability_status', label: 'Dossier Availability Status', type: 'text' },
  ], [supplierOpts, dosageOpts, unitOpts]);

  const toggle = (k) => setEnabled(e => ({ ...e, [k]: !e[k] }));
  const setVal = (k, v) => setValues(vals => ({ ...vals, [k]: v }));

  const save = async () => {
    const payload = {};
    for (const f of BULK_FIELDS) {
      if (enabled[f.key]) {
        const v = values[f.key] ?? '';
        if (String(v).trim() === '') continue;
        payload[f.key] = v;
      }
    }
    if (Object.keys(payload).length === 0) {
      onToast({ type: 'error', msg: 'Enable at least one field to update' });
      return;
    }
    setSaving(true);
    try {
      const res = await bulkUpdateProducts(ids, payload, editorName);
      onSaved(res);
      onToast({ type: 'ok', msg: `${res.updated_count || ids.length} products updated successfully` });
    } catch {
      onToast({ type: 'error', msg: 'Bulk update failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head">
          <h3 className="sup-modal-title">Bulk Edit — {count} Products Selected</h3>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="sup-modal-body">
          {BULK_FIELDS.map(f => {
            const optsLoading = f.type === 'select' && f.options === null;
            const optsEmpty = f.type === 'select' && Array.isArray(f.options) && f.options.length === 0;
            return (
            <div key={f.key} className="d-flex align-items-start gap-3 mb-3">
              <label className="sup-toggle mt-1">
                <input type="checkbox" checked={!!enabled[f.key]} onChange={() => toggle(f.key)} disabled={optsEmpty} />
                <span className="sup-toggle-slider" />
              </label>
              <div className="flex-grow-1">
                <div className="sup-field-label">{f.label}</div>
                {f.type === 'text' && (
                  <input
                    className="sup-input"
                    disabled={!enabled[f.key]}
                    value={values[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                  />
                )}
                {f.type === 'select' && (
                  <SearchableSelect
                    options={f.options || []}
                    value={values[f.key] || ''}
                    onChange={(v) => setVal(f.key, v)}
                    disabled={!enabled[f.key] || optsLoading}
                    loading={optsLoading}
                    emptyLabel={optsEmpty ? 'No options available' : '—'}
                    placeholder="Type to search..."
                  />
                )}
              </div>
            </div>
            );
          })}
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
            <i className="bi bi-x-circle me-1"></i> Cancel
          </button>
          <button type="button" className="sup-btn primary" onClick={save} disabled={saving}>
            <i className="bi bi-check-circle me-1"></i> {saving ? 'Applying…' : 'Apply to All Selected'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Sort helpers ---------- */

const compareBy = (key, dir) => (a, b) => {
  let av = a[key];
  let bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'desc' ? -cmp : cmp;
};

function SortHead({ label, column, sort, onSort, colWidth, onResizeStart, onAutoFit, ...rest }) {
  const active = sort.column === column;
  const icon = active ? (sort.direction === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      className="sup-sort-head"
      style={{ position: 'relative', width: colWidth || undefined, minWidth: 60, ...(rest.style || {}) }}
      {...rest}
      onClick={undefined}
    >
      <span onClick={() => onSort(column)} style={{ cursor: 'pointer', display: 'block', paddingRight: 8 }}>
        {label}
        {active && <span className="sup-sort-ico">{icon}</span>}
      </span>
      {onResizeStart && (
        <span
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onResizeStart(e, column); }}
          onDoubleClick={(e) => { e.stopPropagation(); if (onAutoFit) onAutoFit(column); }}
          style={{ position: 'absolute', top: 0, right: 0, width: 6, height: '100%', cursor: 'col-resize', userSelect: 'none', zIndex: 1 }}
        />
      )}
    </th>
  );
}

/* ---------- Product table ---------- */

const DEFAULT_PAGE_SIZE = 50;

const CSV_EXPORT_COLUMNS = [
  'supplier_name','supplier_product_name','inn','combines_molecules','dosage_form',
  'strength_value_1','strength_unit_code_1','market_auth_countries',
  'patent_status','ma_status','dossier_availability_status','therapeutical_indication',
  'document_name_reference','created_at','created_by','Active',
];

function csvCell(v) {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) v = v.join('; ');
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows) {
  const header = CSV_EXPORT_COLUMNS.join(',');
  const body = rows.map(r => CSV_EXPORT_COLUMNS.map(c => {
    if (c === 'ma_status') return csvCell(r.market_auth_status || r.ma_status || '');
    if (c === 'Active') return csvCell(r.product_inactive === 'Yes' ? 'Inactive' : 'Active');
    return csvCell(r[c]);
  }).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadCsvFile(filename, csvString) {
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function todayStamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function ProductsTable({ supplierName, onBack, dosageForms, strengthUnits }) {
  const { displayName: editorName } = useUser();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [editRow, setEditRow] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [sort, setSort] = useState({ column: null, direction: 'asc' });
  const [filters, setFilters] = useState({ product: [], form: [], type: [], dossier: [], ma: [], maCountries: '', patent: [], dossierAvail: [], dossierPrepDate: '', euAvail: [], hqCountry: [], productName: [], strength: [], unit: [], referenceProduct: [], atcCode: [], atcClassification: [], atcCategory: [], marketAuthStatus: [], therapeuticalIndication: [], patentExpiryDate: [], patentNotes: [], nlMaStatus: [], productId: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hqCountryOptions, setHqCountryOptions] = useState([]);
  const [showFilterSettings, setShowFilterSettings] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const resizeRef = useRef({ key: null, startX: 0, startWidth: 0 });
  const saveWidthsTimer = useRef(null);
  const gearRef = useRef(null);
  const downloadRef = useRef(null);
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);

  const AVAILABLE_FILTERS = useMemo(() => [
    { key: 'productId', label: 'Product ID' },
    { key: 'inn', label: 'INN / Molecule / Product' },
    { key: 'form', label: 'Form' },
    { key: 'type', label: 'Type' },
    { key: 'maStatus', label: 'MA Status' },
    { key: 'marketAuthCountries', label: 'Market Auth Countries' },
    { key: 'patentStatus', label: 'Patent Status' },
    { key: 'dossierAvailability', label: 'Dossier Availability' },
    { key: 'dossierPrepDate', label: 'Dossier Prep Date' },
    { key: 'euMarketAvailability', label: 'EU Market Availability' },
    { key: 'hqCountry', label: 'Supplier HQ' },
    { key: 'productName', label: 'Product Name' },
    { key: 'strength', label: 'Strength' },
    { key: 'unit', label: 'Unit' },
    { key: 'referenceProduct', label: 'Reference Product' },
    { key: 'atcCode', label: 'ATC Code' },
    { key: 'atcClassification', label: 'ATC Classification' },
    { key: 'atcCategory', label: 'ATC Category' },
    { key: 'marketAuthStatus', label: 'Market Authentication Status' },
    { key: 'therapeuticalIndication', label: 'Therepeutical Indication' },
    { key: 'patentExpiryDate', label: 'Patent Expiry Date' },
    { key: 'patentNotes', label: 'Patent Notes' },
    { key: 'nlMaStatus', label: 'NL MA Status' },
  ], []);
  const ALL_FILTER_KEYS = useMemo(() => AVAILABLE_FILTERS.map(f => f.key), [AVAILABLE_FILTERS]);
  const [visibleFilters, setVisibleFilters] = useState(() => ALL_FILTER_KEYS);
  const [pendingFilters, setPendingFilters] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  const location = useLocation();
  const { searchTerm } = useSearch();

  const load = useCallback(async () => {
    setRows(null); setErr('');
    try {
      const data = await listSupplierProducts(supplierName);
      setRows(data);
    } catch {
      setErr('Failed to load products.');
    }
  }, [supplierName]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetchHqCountries().then(setHqCountryOptions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Close dropdown/gear on outside click
  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) setShowDownloadMenu(false);
      if (gearRef.current && !gearRef.current.contains(e.target)) { setShowFilterSettings(false); setPendingFilters(null); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load filter preferences
  useEffect(() => {
    let userId = null;
    try { /* MSAL placeholder */ } catch { /* ignore */ }
    if (!userId) {
      if (!localStorage.getItem('focusone_uid')) localStorage.setItem('focusone_uid', 'u_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
      userId = localStorage.getItem('focusone_uid');
    }
    setCurrentUserId(userId);
    fetch(`${import.meta.env.VITE_API_URL}/api/preferences/${encodeURIComponent(userId)}/supplierDetailFilters`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data && data.preference_value) {
          const saved = JSON.parse(data.preference_value);
          if (Array.isArray(saved) && saved.length > 0) { setVisibleFilters(saved); return; }
        }
        setVisibleFilters(ALL_FILTER_KEYS);
      })
      .catch(() => setVisibleFilters(ALL_FILTER_KEYS));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const saveFilterPreferences = async () => {
    if (!pendingFilters || !currentUserId) return;
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: currentUserId, key: 'supplierDetailFilters', value: JSON.stringify(pendingFilters) }),
      });
      if (!response.ok) throw new Error('Save failed');
      setVisibleFilters(pendingFilters);
      setPendingFilters(null);
      setShowFilterSettings(false);
    } catch {
      setToast({ type: 'error', msg: 'Failed to save filter preferences.' });
    }
  };

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setPage(1); }, [filters, sort, searchTerm]);

  const rowsSafe = useMemo(() => rows || [], [rows]);

  const formOptions    = useMemo(() => uniqueValues(rowsSafe, 'dosage_form'), [rowsSafe]);
  const dossierOptions = useMemo(() => uniqueValues(rowsSafe, 'dossier_availability_status'), [rowsSafe]);
  const maOptions      = useMemo(() => {
    const set = new Set();
    rowsSafe.forEach(r => { const v = r.market_auth_status || r.ma_status; if (v) set.add(String(v)); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rowsSafe]);
  const productOptions = useMemo(() => {
    const set = new Set();
    for (const r of rowsSafe) {
      if (r.inn && String(r.inn).trim()) set.add(String(r.inn));
      if (r.molecule_name && String(r.molecule_name).trim()) set.add(String(r.molecule_name));
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rowsSafe]);
  const patentOptions   = useMemo(() => uniqueValues(rowsSafe, 'patent_status'), [rowsSafe]);
  const dossierAvailOpt = useMemo(() => uniqueValues(rowsSafe, 'dossier_availability_status'), [rowsSafe]);
  const hqCountryEffective = useMemo(() => hqCountryOptions.length > 0 ? hqCountryOptions : uniqueValues(rowsSafe, 'supplier_headquarter_country'), [hqCountryOptions, rowsSafe]);
  const productNameOptions       = useMemo(() => uniqueValues(rowsSafe, 'supplier_product_name'), [rowsSafe]);
  const strengthOptions          = useMemo(() => uniqueValues(rowsSafe, 'strength_value_1'), [rowsSafe]);
  const unitOptions              = useMemo(() => uniqueValues(rowsSafe, 'strength_unit_code_1'), [rowsSafe]);
  const referenceProductOptions  = useMemo(() => uniqueValues(rowsSafe, 'reference_product_name'), [rowsSafe]);
  const atcCodeOptions           = useMemo(() => uniqueValues(rowsSafe, 'atc_code'), [rowsSafe]);
  const atcClassificationOptions = useMemo(() => uniqueValues(rowsSafe, 'atc_classification_name'), [rowsSafe]);
  const atcCategoryOptions       = useMemo(() => uniqueValues(rowsSafe, 'atc_category'), [rowsSafe]);
  const marketAuthStatusOptions  = useMemo(() => uniqueValues(rowsSafe, 'market_auth_status'), [rowsSafe]);
  const therIndOptions           = useMemo(() => uniqueValues(rowsSafe, 'therapeutical_indication'), [rowsSafe]);
  const patentExpiryOptions      = useMemo(() => uniqueValues(rowsSafe, 'patent_expiry_date'), [rowsSafe]);
  const patentNotesOptions       = useMemo(() => uniqueValues(rowsSafe, 'patent_notes'), [rowsSafe]);
  const nlMaStatusOptions        = useMemo(() => uniqueValues(rowsSafe, 'nl_general_ma_status'), [rowsSafe]);

  const euAvailability = (countries) => {
    if (!countries || String(countries).trim() === '') return 'N/A';
    return String(countries).toLowerCase().includes('eu') ? 'YES' : 'No';
  };

  // Dual scrollbar sync
  const syncFromTop = useCallback(() => { if (tableScrollRef.current && topScrollRef.current) tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft; }, []);
  const syncFromTable = useCallback(() => { if (tableScrollRef.current && topScrollRef.current) topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft; }, []);

  // Column resize handlers
  const onResizing = useCallback((e) => {
    const { key, startX, startWidth } = resizeRef.current;
    if (!key) return;
    const next = Math.max(60, startWidth + (e.clientX - startX));
    setColumnWidths(w => ({ ...w, [key]: next }));
  }, []);

  const saveColumnWidths = useCallback((widths) => {
    if (!currentUserId) return;
    clearTimeout(saveWidthsTimer.current);
    saveWidthsTimer.current = setTimeout(() => {
      fetch(`${import.meta.env.VITE_API_URL}/api/preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: currentUserId, key: 'supplierDetailColumnWidths', value: JSON.stringify(widths) }),
      }).catch(() => {});
    }, 300);
  }, [currentUserId]);

  const stopResize = useCallback(() => {
    resizeRef.current = { key: null, startX: 0, startWidth: 0 };
    window.removeEventListener('mousemove', onResizing);
    window.removeEventListener('mouseup', stopResize);
    setColumnWidths(w => { saveColumnWidths(w); return w; });
  }, [onResizing, saveColumnWidths]);

  const startResize = useCallback((e, key) => {
    const th = e.currentTarget.parentElement;
    resizeRef.current = { key, startX: e.clientX, startWidth: th.getBoundingClientRect().width };
    window.addEventListener('mousemove', onResizing);
    window.addEventListener('mouseup', stopResize);
  }, [onResizing, stopResize]);

  const autoFitColumn = useCallback((key) => {
    if (!tableScrollRef.current) return;
    const table = tableScrollRef.current.querySelector('table');
    if (!table) return;
    const headers = table.querySelectorAll('th');
    let colIdx = -1;
    headers.forEach((th, i) => { if (th.dataset.col === key) colIdx = i; });
    if (colIdx < 0) return;
    let maxW = 60;
    table.querySelectorAll('tbody tr').forEach(tr => {
      const td = tr.children[colIdx];
      if (td) maxW = Math.max(maxW, td.scrollWidth + 16);
    });
    maxW = Math.min(maxW, 600);
    setColumnWidths(w => { const n = { ...w, [key]: maxW }; saveColumnWidths(n); return n; });
  }, [saveColumnWidths]);

  // Load column width preferences
  useEffect(() => {
    if (!currentUserId) return;
    fetch(`${import.meta.env.VITE_API_URL}/api/preferences/${encodeURIComponent(currentUserId)}/supplierDetailColumnWidths`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => {
        if (data && data.preference_value) {
          const saved = JSON.parse(data.preference_value);
          if (saved && typeof saved === 'object') setColumnWidths(saved);
        }
      })
      .catch(() => {});
  }, [currentUserId]);

  const filtered = useMemo(() => {
    let arr = rowsSafe;
    const q = (searchTerm || '').trim().toLowerCase();
    if (q) {
      arr = arr.filter(r => {
        const name = String(r.supplier_product_name || '').toLowerCase();
        const inn  = String(r.inn || '').toLowerCase();
        const mol  = String(r.molecule_name || '').toLowerCase();
        const form = String(r.dosage_form || '').toLowerCase();
        return name.includes(q) || inn.includes(q) || mol.includes(q) || form.includes(q);
      });
    }
    const pidQ = (filters.productId || '').trim();
    if (pidQ) {
      arr = arr.filter(r => String(r.product_id || '').startsWith(pidQ));
    }
    if (filters.product && filters.product.length > 0) {
      arr = arr.filter(r => {
        const inn = String(r.inn || '').toLowerCase();
        const mol = String(r.molecule_name || '').toLowerCase();
        const pname = String(r.supplier_product_name || '').toLowerCase();
        return filters.product.some(term => { const t = String(term).toLowerCase(); return inn.includes(t) || mol.includes(t) || pname.includes(t); });
      });
    }
    if (filters.form.length > 0) arr = arr.filter(r => filters.form.includes(String(r.dosage_form || '')));
    if (filters.type.length > 0) arr = arr.filter(r => { const combined = String(r.combines_molecules || '').toLowerCase() === 'yes'; return filters.type.includes(combined ? 'Combined' : 'Single'); });
    if (filters.dossier.length > 0) arr = arr.filter(r => filters.dossier.includes(String(r.dossier_availability_status || '')));
    if (filters.ma.length > 0) arr = arr.filter(r => filters.ma.includes(String(r.market_auth_status || r.ma_status || '')));
    const macQ = (filters.maCountries || '').trim().toLowerCase();
    if (macQ) arr = arr.filter(r => String(r.market_auth_countries || '').toLowerCase().includes(macQ));
    if (filters.patent.length > 0) arr = arr.filter(r => filters.patent.includes(String(r.patent_status || '')));
    if (filters.dossierAvail.length > 0) arr = arr.filter(r => filters.dossierAvail.includes(String(r.dossier_availability_status || '')));
    const dpQ = (filters.dossierPrepDate || '').trim().toLowerCase();
    if (dpQ) arr = arr.filter(r => String(r.dossier_preparation_planned_date || '').toLowerCase().includes(dpQ));
    if (filters.euAvail.length > 0) arr = arr.filter(r => filters.euAvail.includes(euAvailability(r.market_auth_countries)));
    if (filters.hqCountry.length > 0) arr = arr.filter(r => filters.hqCountry.includes(String(r.supplier_headquarter_country || r.hq_country || '')));
    if (filters.productName.length > 0) arr = arr.filter(r => filters.productName.includes(String(r.supplier_product_name || '')));
    if (filters.strength.length > 0) arr = arr.filter(r => filters.strength.includes(String(r.strength_value_1 ?? '')));
    if (filters.unit.length > 0) arr = arr.filter(r => filters.unit.includes(String(r.strength_unit_code_1 || '')));
    if (filters.referenceProduct.length > 0) arr = arr.filter(r => filters.referenceProduct.includes(String(r.reference_product_name || '')));
    if (filters.atcCode.length > 0) arr = arr.filter(r => filters.atcCode.includes(String(r.atc_code || '')));
    if (filters.atcClassification.length > 0) arr = arr.filter(r => filters.atcClassification.includes(String(r.atc_classification_name || '')));
    if (filters.atcCategory.length > 0) arr = arr.filter(r => filters.atcCategory.includes(String(r.atc_category || '')));
    if (filters.marketAuthStatus.length > 0) arr = arr.filter(r => filters.marketAuthStatus.includes(String(r.market_auth_status || '')));
    if (filters.therapeuticalIndication.length > 0) arr = arr.filter(r => filters.therapeuticalIndication.includes(String(r.therapeutical_indication || '')));
    if (filters.patentExpiryDate.length > 0) arr = arr.filter(r => filters.patentExpiryDate.includes(String(r.patent_expiry_date || '')));
    if (filters.patentNotes.length > 0) arr = arr.filter(r => filters.patentNotes.includes(String(r.patent_notes || '')));
    if (filters.nlMaStatus.length > 0) arr = arr.filter(r => filters.nlMaStatus.includes(String(r.nl_general_ma_status || '')));
    return arr;
  }, [rowsSafe, searchTerm, filters]);

  const sorted = useMemo(() => {
    if (!sort.column) return filtered;
    return [...filtered].sort(compareBy(sort.column, sort.direction));
  }, [filtered, sort]);

  const totalCount = rowsSafe.length;

  const effectivePageSize = Math.max(1, pageSize || DEFAULT_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
  const safePage  = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * effectivePageSize;
  const pageEnd   = Math.min(pageStart + effectivePageSize, sorted.length);
  const pageRows  = sorted.slice(pageStart, pageEnd);

  // Sync top scrollbar width
  useEffect(() => {
    if (tableScrollRef.current && topScrollRef.current && topScrollRef.current.firstChild) {
      topScrollRef.current.firstChild.style.width = tableScrollRef.current.scrollWidth + 'px';
    }
  }, [pageRows, pageSize]);

  const getDownloadRows = () => { const sel = sorted.filter(r => selected.has(r.id)); return sel.length > 0 ? sel : sorted; };
  const handleDownloadCSV = () => {
    const exportRows = getDownloadRows();
    if (exportRows.length === 0) { setToast({ type: 'error', msg: 'No rows to download' }); return; }
    const safeName = String(supplierName || 'supplier').replace(/[^\w.-]+/g, '_');
    downloadCsvFile(`${safeName}_products_${todayStamp()}.csv`, rowsToCsv(exportRows));
  };

  const filtersActive = Object.entries(filters).some(([, v]) => Array.isArray(v) ? v.length > 0 : !!v);
  const clearFilters = () => setFilters({ product: [], form: [], type: [], dossier: [], ma: [], maCountries: '', patent: [], dossierAvail: [], dossierPrepDate: '', euAvail: [], hqCountry: [], productName: [], strength: [], unit: [], referenceProduct: [], atcCode: [], atcClassification: [], atcCategory: [], marketAuthStatus: [], therapeuticalIndication: [], patentExpiryDate: [], patentNotes: [], nlMaStatus: [], productId: '' });

  const handleSort = (column) => {
    setSort(s => {
      if (s.column !== column) return { column, direction: 'asc' };
      if (s.direction === 'asc') return { column, direction: 'desc' };
      return { column: null, direction: 'asc' };
    });
  };

  const activePageIds = pageRows.filter(r => r.product_inactive !== 'Yes').map(r => r.id);
  const allPageChecked = activePageIds.length > 0 && activePageIds.every(id => selected.has(id));
  const toggleAll = () => { setSelected(prev => { const n = new Set(prev); if (allPageChecked) activePageIds.forEach(id => n.delete(id)); else activePageIds.forEach(id => n.add(id)); return n; }); };
  const toggleOne = (id) => { setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };

  const onRowSaved = (updated) => { setRows(rs => rs.map(r => (r.id === (updated.id || updated.product_id) ? { ...r, ...updated } : r))); setEditRow(null); if (updated.product_inactive === 'Yes') { setSelected(prev => { const n = new Set(prev); n.delete(updated.id || updated.product_id); return n; }); } };
  const onBulkSaved = () => { setBulkOpen(false); setSelected(new Set()); load(); };

  if (err) return (<div className="sup-page"><Header supplierName={supplierName} onBack={onBack} /><div className="sup-error-box">{err}</div></div>);
  if (!rows) return (<div className="sup-page"><Header supplierName={supplierName} onBack={onBack} /><div className="sup-table-wrap p-4">{[0,1,2,3,4].map(i => (<div key={i} className="sup-skeleton mb-2" style={{ height: 28, width: '100%' }} />))}</div></div>);
  if (rows.length === 0) return (<div className="sup-page"><Header supplierName={supplierName} onBack={onBack} /><div className="sup-empty">No products found for this supplier.</div></div>);

  return (
    <div className="sup-page">
      <Header supplierName={supplierName} onBack={onBack} />

      <div className="sup-statsbar">
        <span className="sup-stat-gray">Total: {totalCount} products</span>
      </div>
      <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 8 }}>
        Showing {sorted.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sorted.length} products
      </div>

      {/* Filter controls row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, maxHeight: 140, overflowY: 'auto', overflowX: 'hidden' }}>
          <div className="sup-filters-row" style={{ marginBottom: 0, flexWrap: 'wrap' }}>
            {visibleFilters.includes('productId') && (
              <div style={{ minWidth: 140 }}>
                <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Product ID</div>
                <input className="sup-input" placeholder="Search by ID..." value={filters.productId} onChange={(e) => setFilters(f => ({ ...f, productId: e.target.value.replace(/\D/g, '') }))} style={{ padding: '5px 10px', fontSize: 12.5 }} />
              </div>
            )}
            {visibleFilters.includes('inn') && (<MultiSelect label="INN / Molecule / Product" options={productOptions} values={filters.product || []} onChange={(v) => setFilters(f => ({ ...f, product: v }))} placeholder="All Products" minWidth={220} />)}
            {visibleFilters.includes('form') && (<MultiSelect label="Form" options={formOptions} values={filters.form} onChange={(v) => setFilters(f => ({ ...f, form: v }))} placeholder="All Forms" minWidth={180} />)}
            {visibleFilters.includes('type') && (<MultiSelect label="Type" options={['Single', 'Combined']} values={filters.type} onChange={(v) => setFilters(f => ({ ...f, type: v }))} placeholder="All Types" minWidth={160} />)}
            {visibleFilters.includes('maStatus') && (<MultiSelect label="MA Status" options={maOptions} values={filters.ma} onChange={(v) => setFilters(f => ({ ...f, ma: v }))} placeholder="All MA Status" minWidth={180} />)}
            {visibleFilters.includes('marketAuthCountries') && (<div style={{ minWidth: 180 }}><div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Market Auth Countries</div><input className="sup-input" placeholder="Type to filter…" value={filters.maCountries} onChange={(e) => setFilters(f => ({ ...f, maCountries: e.target.value }))} style={{ padding: '5px 10px', fontSize: 12.5 }} /></div>)}
            {visibleFilters.includes('patentStatus') && (<MultiSelect label="Patent Status" options={patentOptions} values={filters.patent} onChange={(v) => setFilters(f => ({ ...f, patent: v }))} placeholder="All" minWidth={160} />)}
            {visibleFilters.includes('dossierAvailability') && (<MultiSelect label="Dossier Availability" options={dossierAvailOpt} values={filters.dossierAvail} onChange={(v) => setFilters(f => ({ ...f, dossierAvail: v }))} placeholder="All" minWidth={180} />)}
            {visibleFilters.includes('dossierPrepDate') && (<div style={{ minWidth: 160 }}><div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Dossier Prep Date</div><input className="sup-input" placeholder="Type to filter…" value={filters.dossierPrepDate} onChange={(e) => setFilters(f => ({ ...f, dossierPrepDate: e.target.value }))} style={{ padding: '5px 10px', fontSize: 12.5 }} /></div>)}
            {visibleFilters.includes('euMarketAvailability') && (<MultiSelect label="EU Market Availability" options={['YES', 'No', 'N/A']} values={filters.euAvail} onChange={(v) => setFilters(f => ({ ...f, euAvail: v }))} placeholder="All" minWidth={180} />)}
            {visibleFilters.includes('hqCountry') && (<MultiSelect label="Supplier HQ" options={hqCountryEffective} values={filters.hqCountry} onChange={(v) => setFilters(f => ({ ...f, hqCountry: v }))} placeholder="All" minWidth={160} />)}
            {visibleFilters.includes('productName') && (<MultiSelect label="Product Name" options={productNameOptions} values={filters.productName} onChange={(v) => setFilters(f => ({ ...f, productName: v }))} placeholder="All" minWidth={180} />)}
            {visibleFilters.includes('strength') && (<MultiSelect label="Strength" options={strengthOptions} values={filters.strength} onChange={(v) => setFilters(f => ({ ...f, strength: v }))} placeholder="All" minWidth={140} />)}
            {visibleFilters.includes('unit') && (<MultiSelect label="Unit" options={unitOptions} values={filters.unit} onChange={(v) => setFilters(f => ({ ...f, unit: v }))} placeholder="All" minWidth={140} />)}
            {visibleFilters.includes('referenceProduct') && (<MultiSelect label="Reference Product" options={referenceProductOptions} values={filters.referenceProduct} onChange={(v) => setFilters(f => ({ ...f, referenceProduct: v }))} placeholder="All" minWidth={180} />)}
            {visibleFilters.includes('atcCode') && (<MultiSelect label="ATC Code" options={atcCodeOptions} values={filters.atcCode} onChange={(v) => setFilters(f => ({ ...f, atcCode: v }))} placeholder="All" minWidth={140} />)}
            {visibleFilters.includes('atcClassification') && (<MultiSelect label="ATC Classification" options={atcClassificationOptions} values={filters.atcClassification} onChange={(v) => setFilters(f => ({ ...f, atcClassification: v }))} placeholder="All" minWidth={180} />)}
            {visibleFilters.includes('atcCategory') && (<MultiSelect label="ATC Category" options={atcCategoryOptions} values={filters.atcCategory} onChange={(v) => setFilters(f => ({ ...f, atcCategory: v }))} placeholder="All" minWidth={160} />)}
            {visibleFilters.includes('marketAuthStatus') && (<MultiSelect label="Market Authentication Status" options={marketAuthStatusOptions} values={filters.marketAuthStatus} onChange={(v) => setFilters(f => ({ ...f, marketAuthStatus: v }))} placeholder="All" minWidth={200} />)}
            {visibleFilters.includes('therapeuticalIndication') && (<MultiSelect label="Therepeutical Indication" options={therIndOptions} values={filters.therapeuticalIndication} onChange={(v) => setFilters(f => ({ ...f, therapeuticalIndication: v }))} placeholder="All" minWidth={200} />)}
            {visibleFilters.includes('patentExpiryDate') && (<MultiSelect label="Patent Expiry Date" options={patentExpiryOptions} values={filters.patentExpiryDate} onChange={(v) => setFilters(f => ({ ...f, patentExpiryDate: v }))} placeholder="All" minWidth={160} />)}
            {visibleFilters.includes('patentNotes') && (<MultiSelect label="Patent Notes" options={patentNotesOptions} values={filters.patentNotes} onChange={(v) => setFilters(f => ({ ...f, patentNotes: v }))} placeholder="All" minWidth={160} />)}
            {visibleFilters.includes('nlMaStatus') && (<MultiSelect label="NL MA Status" options={nlMaStatusOptions} values={filters.nlMaStatus} onChange={(v) => setFilters(f => ({ ...f, nlMaStatus: v }))} placeholder="All" minWidth={160} />)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, paddingTop: 18 }}>
          {filtersActive && (<button type="button" className="sup-clear-link" onClick={clearFilters}>Clear All Filters</button>)}
          <div ref={gearRef} style={{ position: 'relative' }}>
            <button onClick={() => setShowFilterSettings(prev => !prev)} className="btn btn-sm" style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: 'var(--t3)' }} title="Customize filters"><i className="bi bi-gear"></i></button>
            {showFilterSettings && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 220, zIndex: 1000, padding: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', marginBottom: 8 }}>Customize Filters</div>
                <div className="customize-filters-scroll" style={{ maxHeight: 280, overflowY: 'scroll', overflowX: 'hidden' }}>
                {AVAILABLE_FILTERS.map(f => (
                  <label key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={(pendingFilters || visibleFilters).includes(f.key)} onChange={() => { const current = pendingFilters || visibleFilters; const updated = current.includes(f.key) ? current.filter(k => k !== f.key) : [...current, f.key]; setPendingFilters(updated); }} style={{ accentColor: 'var(--teal)', width: 14, height: 14 }} />
                    {f.label}
                  </label>
                ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                  <button onClick={saveFilterPreferences} disabled={!pendingFilters} style={{ background: pendingFilters ? 'var(--teal)' : 'var(--t3)', color: 'white', border: 'none', borderRadius: 6, padding: '6px 16px', fontSize: 12, cursor: pendingFilters ? 'pointer' : 'not-allowed', fontWeight: 500, flex: 1 }}><i className="bi bi-check2 me-1"></i>Save</button>
                  <button onClick={() => { setPendingFilters(null); setShowFilterSettings(false); }} style={{ background: 'var(--card)', color: 'var(--t3)', border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
          <div ref={downloadRef} style={{ position: 'relative' }}>
            <button onClick={() => setShowDownloadMenu(prev => !prev)} className="btn btn-sm" style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} title="Download"><i className="bi bi-download"></i></button>
            {showDownloadMenu && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 140, zIndex: 1000, overflow: 'hidden' }}>
                <button onClick={() => { handleDownloadCSV(); setShowDownloadMenu(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 14px', border: 'none', background: 'var(--card)', cursor: 'pointer', fontSize: 13, color: 'var(--t1)' }} onMouseEnter={e => e.currentTarget.style.background = '#EFF8FF'} onMouseLeave={e => e.currentTarget.style.background = 'var(--card)'}><i className="bi bi-file-earmark-spreadsheet" style={{ color: 'var(--teal)' }}></i>Download CSV</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="sup-selbar">
          <span>{selected.size} items selected</span>
          <button type="button" className="sup-btn primary" onClick={() => setBulkOpen(true)}>Edit Selected</button>
        </div>
      )}

      <div className="sup-table-wrap">
        <style>{`
.table-scroll-container::-webkit-scrollbar { height: 8px; width: 8px; }
.table-scroll-container::-webkit-scrollbar-track { background: #f1f1f1; }
.table-scroll-container::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-scroll-container::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.table-top-scroll::-webkit-scrollbar { height: 8px; }
.table-top-scroll::-webkit-scrollbar-track { background: #f1f1f1; }
.table-top-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.table-top-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.customize-filters-scroll::-webkit-scrollbar { width: 8px; }
.customize-filters-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.customize-filters-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 4px; }
.customize-filters-scroll::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
.customize-filters-scroll { scrollbar-width: thin; scrollbar-color: #c1c1c1 #f1f1f1; }
.sup-table td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }
        `}</style>
        <div ref={topScrollRef} className="table-top-scroll" onScroll={syncFromTop} style={{ overflowX: 'auto', overflowY: 'hidden', height: 12, marginBottom: 4 }}><div style={{ height: 1 }} /></div>
        <div ref={tableScrollRef} className="table-scroll-container" onScroll={syncFromTable} style={{ overflowX: 'auto', overflowY: 'auto', width: '100%', WebkitOverflowScrolling: 'touch', maxHeight: '72vh' }}>
          <table className="sup-table" style={{ minWidth: 4600, tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: 40 }}><input type="checkbox" checked={allPageChecked} onChange={toggleAll} aria-label="Select all on page" /></th>
                <th style={{ width: 60 }}>Open</th>
                <th style={{ width: 60 }}>Remove</th>
                {[
                  ['Status','product_status'],['Active','product_inactive'],['ID','product_id'],['Supplier ID','supplier_id'],
                  ['File Type','source_type'],['Document Name','document_name_reference'],
                  ['Supplier HQ','supplier_headquarter_country'],
                  ['Product Name','supplier_product_name'],['INN','inn'],
                  ['Combine Molecule Type','combines_molecules'],['Strength','strength_value_1'],
                  ['Unit','strength_unit_code_1'],['Dosage Form','dosage_form'],
                  ['Full Strength','full_strength'],['Pack Size','pack_size'],
                  ['Reference Product','reference_product_name'],['ATC Code','atc_code'],
                  ['ATC Classification','atc_classification_name'],['ATC Category','atc_category'],
                  ['Market Authentication Countries','market_auth_countries'],
                  ['Market Authentication Status','market_auth_status'],
                  ['Manufacturing Sites','manufacturing_sites'],
                  ['Therepeutical Indication','therapeutical_indication'],
                  ['Patent Expiry Date','patent_expiry_date'],['Patent Status','patent_status'],
                  ['Patent Notes','patent_notes'],['NL Market','nl_market_available'],
                  ['MA Status','supplier_ma_status'],['NL MA Status','nl_general_ma_status'],
                  ['MA Reg. Number','ma_registration_number'],['MA Approval','ma_approved_by'],
                  ['MA Additional Info','ma_additional_info'],
                  ['Dossier Availability','dossier_availability_status'],
                  ['Dossier Planned Date','dossier_preparation_planned_date'],
                  ['Additional Comments','additional_comments'],
                  ['First Creation Date','created_at'],['Update Date','updated_at'],
                  ['Created/Update by','created_by'],
                ].map(([lbl, col]) => (
                  <SortHead key={col} label={lbl} column={col} sort={sort} onSort={handleSort}
                    colWidth={columnWidths[col]} onResizeStart={startResize} onAutoFit={autoFitColumn}
                    data-col={col} />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map(r => {
                const isSel = selected.has(r.id);
                const combined = String(r.combines_molecules || '').toLowerCase() === 'yes';
                const nl = isNL(r.market_auth_countries);
                const supplierMaCls = maBadgeClass(r.supplier_ma_status);
                return (
                  <tr key={r.id} className={isSel ? 'selected' : ''}>
                    <td><input type="checkbox" checked={isSel} onChange={() => toggleOne(r.id)} disabled={r.product_inactive === 'Yes'} title={r.product_inactive === 'Yes' ? 'Inactive products cannot be selected for bulk actions' : undefined} style={r.product_inactive === 'Yes' ? { cursor: 'not-allowed', opacity: 0.4 } : undefined} /></td>
                    <td><button type="button" className="btn btn-sm" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 8px', background: 'white' }} title="Edit" onClick={() => setEditRow(r)}><i className="bi bi-box-arrow-up-right" style={{ color: 'var(--teal)', fontSize: 14 }}></i></button></td>
                    <td><button type="button" className="btn btn-sm" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 8px', background: 'white', cursor: r.product_inactive === 'Yes' ? 'not-allowed' : 'pointer', opacity: r.product_inactive === 'Yes' ? 0.4 : 1 }} title="Remove product" disabled={r.product_inactive === 'Yes'} onClick={() => setRemoveConfirm(r)} onMouseEnter={e => { if (r.product_inactive !== 'Yes') e.currentTarget.querySelector('i').style.color = '#dc2626'; }} onMouseLeave={e => { e.currentTarget.querySelector('i').style.color = '#9ca3af'; }}><i className="bi bi-trash" style={{ color: '#9ca3af', fontSize: 14 }}></i></button></td>
                    <td>{r.product_inactive === 'Yes' ? <span title="Reactivate product to change status" style={{ display: 'inline-block', width: 110, padding: '3px 6px', fontSize: 12, color: 'var(--t3)', fontWeight: 500, cursor: 'not-allowed' }}>{normalizeStatus(r.product_status)}</span> : <select value={normalizeStatus(r.product_status)} disabled={!r.id} onChange={(e) => { const newVal = e.target.value; if (newVal === normalizeStatus(r.product_status)) return; setStatusConfirm({ row: r, newStatus: newVal, oldStatus: r.product_status }); }} style={{ width: 110, padding: '3px 6px', fontSize: 12, border: '1px solid #dee2e6', borderRadius: 6, background: 'white', color: statusColor(normalizeStatus(r.product_status)), fontWeight: 500, cursor: 'pointer' }}>{STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}</select>}</td>
                    <td>{r.product_inactive === 'Yes' ? <span style={{ color: '#dc2626', fontSize: 16 }} aria-label="Inactive">&#10060;</span> : <span style={{ color: '#16a34a', fontSize: 16 }} aria-label="Active">&#9989;</span>}</td>
                    <td>{r.product_id || '—'}</td>
                    <td>{r.supplier_id || '—'}</td>
                    <td>{r.source_type || '—'}</td>
                    <td>{r.document_name_reference ? (<a href={`${import.meta.env.VITE_API_URL}/api/documents/${encodeURIComponent(r.document_name_reference)}`} target="_blank" rel="noopener noreferrer" title={r.document_name_reference} className="sup-truncate" style={{ color: '#1d4ed8', textDecoration: 'none', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>{truncate(r.document_name_reference, 30)}</a>) : '—'}</td>
                    <td>{r.supplier_headquarter_country || '—'}</td>
                    <td><span className="sup-prod-name">{r.supplier_product_name || '—'}</span></td>
                    <td><span className="sup-inn">{r.inn || '—'}</span></td>
                    <td><span className={`sup-badge ${combined ? 'purple-soft' : 'teal-soft'}`}>{combined ? 'Combined' : 'Single'}</span></td>
                    <td>{r.strength_value_1 ?? '—'}</td>
                    <td>{r.strength_unit_code_1 || '—'}</td>
                    <td>{r.dosage_form || '—'}</td>
                    <td><span className="sup-truncate" title={r.full_strength || ''}>{truncate(r.full_strength, 30)}</span></td>
                    <td>{r.pack_size || '—'}</td>
                    <td><span className="sup-truncate" title={r.reference_product_name || ''}>{truncate(r.reference_product_name, 30)}</span></td>
                    <td>{r.atc_code || '—'}</td>
                    <td><span className="sup-truncate" title={r.atc_classification_name || ''}>{truncate(r.atc_classification_name, 30)}</span></td>
                    <td>{r.atc_category || '—'}</td>
                    <td><span className="sup-truncate" title={r.market_auth_countries || ''}>{truncate(r.market_auth_countries, 30)}</span></td>
                    <td>{r.market_auth_status || '—'}</td>
                    <td><span className="sup-truncate" title={r.manufacturing_sites || ''}>{truncate(r.manufacturing_sites, 30)}</span></td>
                    <td><span className="sup-truncate" title={arrOrStr(r.therapeutical_indication)}>{truncate(arrOrStr(r.therapeutical_indication), 30)}</span></td>
                    <td>{r.patent_expiry_date || '—'}</td>
                    <td>{r.patent_status || '—'}</td>
                    <td><span className="sup-truncate" title={r.patent_notes || ''}>{truncate(r.patent_notes, 30)}</span></td>
                    <td>{nl ? <span className="sup-check" aria-label="Yes">✓</span> : <span className="sup-cross" aria-label="No">✗</span>}</td>
                    <td>{r.supplier_ma_status ? (supplierMaCls ? <span className={`sup-badge ${supplierMaCls}`}>{r.supplier_ma_status}</span> : <span>{r.supplier_ma_status}</span>) : '—'}</td>
                    <td>{r.nl_general_ma_status || '—'}</td>
                    <td>{r.ma_registration_number || '—'}</td>
                    <td>{r.ma_approved_by || '—'}</td>
                    <td><span className="sup-truncate" title={r.ma_additional_info || ''}>{truncate(r.ma_additional_info, 30)}</span></td>
                    <td>{r.dossier_availability_status || '—'}</td>
                    <td>{r.dossier_preparation_planned_date || '—'}</td>
                    <td><span className="sup-truncate" title={arrOrStr(r.additional_comments)}>{truncate(arrOrStr(r.additional_comments), 30)}</span></td>
                    <td>{fmtDate(r.created_at)}</td>
                    <td>{fmtDate(r.updated_at)}</td>
                    <td>{r.created_by || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
          <button type="button" onClick={() => { if (pageSize < sorted.length) setPageSize(sorted.length); else setPageSize(DEFAULT_PAGE_SIZE); setPage(1); }} style={{ fontSize: 12, color: 'var(--teal)', border: '1px solid var(--teal)', background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>{pageSize < sorted.length ? 'Show All' : `Show ${DEFAULT_PAGE_SIZE}`}</button>
          <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
            <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
            {(() => { const win = 5; let s = Math.max(1, safePage - Math.floor(win / 2)); let e = Math.min(pageCount, s + win - 1); s = Math.max(1, e - win + 1); const nums = []; for (let i = s; i <= e; i++) nums.push(i); return nums.map(n => (<button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>)); })()}
            <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
          </nav>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>Showing {sorted.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sorted.length.toLocaleString('en-US')} {sorted.length === 1 ? 'product' : 'products'}</span>
        </div>
      </div>

      {editRow && (<SingleEditModal row={editRow} onClose={() => setEditRow(null)} onSaved={onRowSaved} onToast={setToast} dosageForms={dosageForms} strengthUnits={strengthUnits} editorName={editorName} />)}
      {bulkOpen && (<BulkEditModal count={selected.size} ids={Array.from(selected)} onClose={() => setBulkOpen(false)} onSaved={onBulkSaved} onToast={setToast} editorName={editorName} />)}
      {removeConfirm && (
        <RemoveConfirmModal
          row={removeConfirm}
          onCancel={() => setRemoveConfirm(null)}
          onConfirm={async () => {
            const r = removeConfirm;
            setRemoveConfirm(null);
            try {
              await deleteProduct(r.id, editorName);
              setRows(rs => rs.map(x => x.id === r.id ? { ...x, product_inactive: 'Yes', product_deletion_date: new Date().toISOString(), deleted_by: editorName } : x));
              setSelected(prev => { const n = new Set(prev); n.delete(r.id); return n; });
              setToast({ type: 'ok', msg: 'Product marked as inactive.' });
            } catch (err) {
              setToast({ type: 'error', msg: err?.response?.data?.error || 'Failed to remove product' });
            }
          }}
        />
      )}
      {statusConfirm && (
        <StatusConfirmModal
          productName={statusConfirm.row.supplier_product_name}
          newStatus={statusConfirm.newStatus}
          onCancel={() => setStatusConfirm(null)}
          onConfirm={async () => {
            const { row: r, newStatus, oldStatus } = statusConfirm;
            setRows(rs => rs.map(x => x.id === r.id ? { ...x, product_status: newStatus } : x));
            setStatusConfirm(null);
            try { await patchProductStatus(r.id, newStatus, editorName); }
            catch { setRows(rs => rs.map(x => x.id === r.id ? { ...x, product_status: oldStatus } : x)); setToast({ type: 'error', msg: 'Failed to update status' }); }
          }}
        />
      )}
      {toast && (<div className={`sup-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>)}
    </div>
  );
}

function Header({ supplierName, onBack }) {
  return (
    <div className="d-flex align-items-center gap-3 mb-3">
      <button type="button" className="sup-back-btn" onClick={onBack}>← Back</button>
      <div className="sup-breadcrumb">Suppliers &rsaquo; <strong>{supplierName}</strong></div>
    </div>
  );
}

/* ---------- Main page ---------- */

export default function Suppliers() {
  const { instance: msalInstance } = useMsal();
  const [suppliers, setSuppliers] = useState(null);
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState('');
  const [active, setActive] = useState(null);
  const [infoName, setInfoName] = useState(null);
  const [toast, setToast] = useState(null);
  const [dosageForms, setDosageForms] = useState([]);
  const [strengthUnits, setStrengthUnits] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]); // Active / Partial / Update Needed
  const [localSearch, setLocalSearch] = useState('');
  const [totalProducts, setTotalProducts] = useState(null);
  const [extCounts, setExtCounts] = useState(null);
  const { searchTerm } = useSearch();
  const navigate = useNavigate();

  const loadSuppliers = useCallback(async () => {
    setErr('');
    setSuppliers(null);
    setStats(null);
    try {
      const data = await listSuppliers();
      setSuppliers(Array.isArray(data?.rows) ? data.rows : []);
      setStats(data?.stats || null);
    } catch {
      setErr('Failed to load suppliers. Please refresh.');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await loadSuppliers();
    })();
    return () => { alive = false; };
  }, [loadSuppliers]);

  // Read Nova-driven filter on mount — populate visual filter states
  useEffect(() => {
    const raw = sessionStorage.getItem('novaFilter');
    if (!raw) return;
    try {
      const f = JSON.parse(raw);
      if (f.supplier) setLocalSearch(String(f.supplier));
      if (f.search) setMoleculeSearch(String(f.search));
    } catch { /* ignore */ }
    sessionStorage.removeItem('novaFilter');
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    fetchDosageForms().then(setDosageForms).catch(() => {});
    fetchStrengthUnits().then(setStrengthUnits).catch(() => {});
    getDashboardToken(msalInstance).then(token => fetchDashboardStats(token)).then(d => setTotalProducts(d.totalSupplierProducts)).catch(() => {});
    fetchRegisterCounts().then(setExtCounts).catch(() => {});
  }, []);

  const maxCount = useMemo(() => {
    if (!suppliers || suppliers.length === 0) return 0;
    return suppliers.reduce((m, s) => Math.max(m, Number(s.product_count) || 0), 0);
  }, [suppliers]);

  const visibleSuppliers = useMemo(() => {
    if (!suppliers) return null;
    const topQ = (searchTerm || '').trim().toLowerCase();
    const localQ = (localSearch || '').trim().toLowerCase();
    let arr = suppliers;
    if (topQ) arr = arr.filter(s => String(s.supplier_name || '').toLowerCase().includes(topQ));
    if (localQ) arr = arr.filter(s => String(s.supplier_name || '').toLowerCase().includes(localQ));
    if (statusFilter.length > 0) {
      const want = statusFilter.map(s => s.toLowerCase());
      arr = arr.filter(s => want.includes(statusFromCount(s.product_count).label.toLowerCase()));
    }
    return arr;
  }, [suppliers, searchTerm, localSearch, statusFilter]);

  const filtersActive = statusFilter.length > 0 || localSearch.trim() !== '';
  const clearFilters = () => { setStatusFilter([]); setLocalSearch(''); };

  if (active) {
    return (
      <ProductsTable
        supplierName={active}
        onBack={() => setActive(null)}
        dosageForms={dosageForms}
        strengthUnits={strengthUnits}
      />
    );
  }

  const nlPct = stats ? Number(stats.overallNLPercent || 0) : 0;
  const nlBarCls = progressClass(nlPct);
  const sc = stats?.statusCounts || { active: 0, partial: 0, updateNeeded: 0 };

  return (
    <div className="sup-page">
      <h2 className="sup-heading mb-3">Suppliers</h2>

      {err && (
        <div className="sup-error-box mb-3">
          <div>{err}</div>
          <button
            type="button"
            className="sup-btn primary mt-2"
            onClick={() => loadSuppliers()}
          >
            <i className="bi bi-arrow-clockwise me-1"></i> Retry
          </button>
        </div>
      )}

      {/* KPI cards */}
      {stats && (
        <div className="row g-3 mb-3">
          <div className="col-xl col-lg-6 col-md-6 col-sm-12">
            <div
              className="sup-kpi"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/supplier-products')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/supplier-products'); }}
              style={{ cursor: 'pointer' }}
            >
              <div className="sup-kpi-ico"><span style={{ fontSize: 22 }}>💊</span></div>
              <div className="sup-kpi-lbl">Total Products of Supplier</div>
              <div className="sup-kpi-val">{totalProducts != null ? totalProducts.toLocaleString('en-US') : '—'}</div>
              <div style={{ color: 'var(--teal)', fontSize: 12, marginTop: 4 }}>Click here to display all supplier products</div>
            </div>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-12">
            <div
              className="sup-kpi"
              role="button"
              tabIndex={0}
              onClick={() => navigate('/suppliers-list')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/suppliers-list'); }}
              style={{ cursor: 'pointer' }}
            >
              <div className="sup-kpi-ico"><span style={{ fontSize: 22 }}>🏭</span></div>
              <div className="sup-kpi-lbl">Total Suppliers</div>
              <div className="sup-kpi-val">{stats.totalSuppliers}</div>
            </div>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-12">
            <div className="sup-kpi">
              <div className="sup-kpi-ico"><span style={{ fontSize: 22 }}>📅</span></div>
              <div className="sup-kpi-lbl">Latest Data Upload</div>
              <div className="sup-kpi-val sm">{stats.latestUpload || '—'}</div>
            </div>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-12">
            <div className="sup-kpi">
              <div className="sup-kpi-ico"><span style={{ fontSize: 22 }}>✅</span></div>
              <div className="sup-kpi-lbl">Overall NL Availability</div>
              <div className="sup-kpi-val">{nlPct}%</div>
              <div className={`sup-progress ${nlBarCls} mt-2`}>
                <span style={{ width: `${Math.min(100, Math.max(0, nlPct))}%` }} />
              </div>
            </div>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-12">
            <div className="sup-kpi">
              <div className="sup-kpi-ico"><span style={{ fontSize: 22 }}>📊</span></div>
              <div className="sup-kpi-lbl">Supplier Status</div>
              <div className="sup-kpi-rows">
                <div><span className="sup-status-dot" style={{ background: 'var(--green)' }}></span> Active: <strong>{sc.active}</strong></div>
                <div><span className="sup-status-dot" style={{ background: 'var(--teal)' }}></span> Partial: <strong>{sc.partial}</strong></div>
                <div><span className="sup-status-dot" style={{ background: 'var(--red)' }}></span> Update Needed: <strong>{sc.updateNeeded}</strong></div>
              </div>
            </div>
          </div>
          <div className="col-xl col-lg-6 col-md-6 col-sm-12">
            <div className="sup-kpi">
              <div className="sup-kpi-ico"><span style={{ fontSize: 22 }}>🌐</span></div>
              <div className="sup-kpi-lbl">External Data of Suppliers</div>
              <div className="sup-kpi-rows">
                {[
                  { label: '\u{1F1EA}\u{1F1FA} EMA Register', path: '/ema-supps', count: extCounts?.ema },
                  { label: '\u{1F1F3}\u{1F1F1} Dutch CBG-MEB', path: '/dutch-supps', count: extCounts?.dutch },
                  { label: '\u{1F1EB}\u{1F1F7} ANSM France', path: '/ansm-supps', count: extCounts?.ansm },
                  { label: '\u{1F310} HMA (MRI Product Index)', path: '/hma-supps', count: extCounts?.hma },
                ].map(r => (
                  <div key={r.path} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span role="button" tabIndex={0} onClick={() => navigate(r.path)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(r.path); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, paddingLeft: 2 }}>
                      <span style={{ color: 'var(--teal)' }}>{r.label}</span>
                    </span>
                    <strong>{r.count != null ? r.count.toLocaleString('en-US') : '—'}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      {suppliers && (
        <div className="sup-filters-row">
          <MultiSelect
            label="Status"
            options={['Active', 'Partial', 'Update Needed']}
            values={statusFilter}
            onChange={setStatusFilter}
            placeholder="All Status"
            minWidth={200}
          />
          <div className="ms-wrap" style={{ minWidth: 240 }}>
            <div className="ms-label">Search</div>
            <div className={`ms-control${localSearch ? ' open' : ''}`}>
              <input
                type="text"
                className="ms-input"
                placeholder="Search supplier name…"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
              />
              {localSearch && (
                <button
                  type="button"
                  className="ms-clear"
                  onClick={() => setLocalSearch('')}
                  title="Clear"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          {filtersActive && (
            <button type="button" className="sup-clear-link" onClick={clearFilters}>
              Clear All
            </button>
          )}
        </div>
      )}

      {!suppliers && !err && (
        <div className="row g-3">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div className="col-12 col-md-6 col-xl-4" key={i}><CardSkeleton /></div>
          ))}
        </div>
      )}

      {visibleSuppliers && visibleSuppliers.length === 0 && (
        <div className="sup-empty">
          {suppliers && suppliers.length > 0
            ? 'No suppliers match your search.'
            : 'No suppliers yet.'}
        </div>
      )}

      {visibleSuppliers && visibleSuppliers.length > 0 && (
        <div className="row g-3">
          {visibleSuppliers.map(s => (
            <div className="col-12 col-md-6 col-xl-4" key={s.supplier_name}>
              <SupplierCard
                supplier={s}
                maxCount={maxCount}
                onOpen={(name) => setActive(name)}
                onInfo={(name) => setInfoName(name)}
              />
            </div>
          ))}
        </div>
      )}

      {infoName && (
        <SupplierInfoModal
          supplierName={infoName}
          onClose={() => setInfoName(null)}
          onToast={setToast}
        />
      )}

      {toast && (
        <div className={`sup-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
